const WEBINAR_TIMEZONE_OFFSET_MINUTES = {
  UTC: 0,
  GMT: 0,
  CET: 60,
  CEST: 120,
  EST: -300,
  EDT: -240,
  CST: -360,
  CDT: -300,
  MST: -420,
  MDT: -360,
  PST: -480,
  PDT: -420,
  IST: 330,
  JST: 540,
  AEST: 600,
  AEDT: 660,
  NZST: 720,
  HKT: 480,
  SGT: 480,
  EET: 120,
  WET: 0,
};

const pad = (value) => String(value).padStart(2, '0');
const ISO_HAS_TIMEZONE_SUFFIX = /(Z|[+-]\d{2}:\d{2})$/i;

export const normalizeWebinarUtcIso = (isoString) => {
  const raw = String(isoString || '').trim();
  if (!raw) return '';
  return ISO_HAS_TIMEZONE_SUFFIX.test(raw) ? raw : `${raw}Z`;
};

export const parseWebinarUtcDate = (isoString) => {
  const normalized = normalizeWebinarUtcIso(isoString);
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseDatetimeLocalValue = (value) => {
  const match = String(value || '').match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/
  );
  if (!match) return null;

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
  };
};

export const getWebinarTimezoneOffsetMinutes = (timezoneCode = 'UTC') =>
  WEBINAR_TIMEZONE_OFFSET_MINUTES[timezoneCode] ?? 0;

export const webinarLocalInputToUtcIso = (datetimeLocalValue, timezoneCode) => {
  const parsed = parseDatetimeLocalValue(datetimeLocalValue);
  if (!parsed) return null;

  const offsetMinutes = getWebinarTimezoneOffsetMinutes(timezoneCode);
  const utcMs =
    Date.UTC(
      parsed.year,
      parsed.month - 1,
      parsed.day,
      parsed.hour,
      parsed.minute
    ) -
    offsetMinutes * 60 * 1000;

  const date = new Date(utcMs);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export const utcIsoToWebinarLocalInput = (isoString, timezoneCode) => {
  const date = parseWebinarUtcDate(isoString);
  if (!date) return '';

  const offsetMinutes = getWebinarTimezoneOffsetMinutes(timezoneCode);
  const shifted = new Date(date.getTime() + offsetMinutes * 60 * 1000);

  return `${shifted.getUTCFullYear()}-${pad(
    shifted.getUTCMonth() + 1
  )}-${pad(shifted.getUTCDate())}T${pad(shifted.getUTCHours())}:${pad(
    shifted.getUTCMinutes()
  )}`;
};

export const formatWebinarDateForLocalUser = (
  isoString,
  { includeLocalLabel = false, locale } = {}
) => {
  const date = parseWebinarUtcDate(isoString);
  if (!date) return '';

  const formatted = date.toLocaleString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });

  return includeLocalLabel ? `${formatted} (your local time)` : formatted;
};

export const formatWebinarDateInEventTimezone = (
  isoString,
  timezoneCode,
  { locale } = {}
) => {
  const date = parseWebinarUtcDate(isoString);
  if (!date) return '';

  const offsetMinutes = getWebinarTimezoneOffsetMinutes(timezoneCode);
  const shifted = new Date(date.getTime() + offsetMinutes * 60 * 1000);

  const formatted = shifted.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });

  return timezoneCode ? `${formatted} ${timezoneCode}` : formatted;
};

export const isWebinarDateInFuture = (isoString) => {
  const date = parseWebinarUtcDate(isoString);
  if (!date) return false;
  return date.getTime() > Date.now();
};

export const isWebinarJoinWindowOpen = (
  isoString,
  { leadMinutes = 30, trailingMinutes = 120 } = {}
) => {
  const date = parseWebinarUtcDate(isoString);
  if (!date) return false;

  const now = Date.now();
  const startMs = date.getTime();
  const windowStartMs = startMs - leadMinutes * 60 * 1000;
  const windowEndMs = startMs + trailingMinutes * 60 * 1000;

  return now >= windowStartMs && now <= windowEndMs;
};
